import { Children, cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import { Tab as RBTab, Tabs } from 'react-bootstrap';
import styles from './SmallTabs.module.scss';

interface TabProps<TKey> {
    eventKey: TKey;
    title: string;
    children: ReactNode;
}

export const Tab = <TKey extends string>({ eventKey, title, children }: TabProps<TKey>) => {
    return (
        <RBTab eventKey={eventKey} title={title}>
            {children}
        </RBTab>
    );
};

type TabElement<TKey> = ReactElement<TabProps<TKey>>;

interface Props<TKey> {
    activeKey: TKey;
    children: TabElement<TKey> | TabElement<TKey>[] | any;
    onSelect: (key: TKey) => void;
}

export const SmallTabs = <TKey extends string>({ activeKey, onSelect, children }: Props<TKey>) => {
    // Replace each Tab's children with `null` when it's not the active tab (lazy-mount).
    // We swap the children via cloneElement's 3rd arg rather than passing a custom `isActive`
    // prop, which react-bootstrap would otherwise forward to the underlying DOM node and
    // trigger a "React does not recognize 'isActive' on a DOM element" warning.
    const enhancedChildren = Children.map(children, (child) => {
        if (isValidElement<TabProps<TKey>>(child)) {
            const isActive = child.props.eventKey === activeKey;
            return cloneElement(child, {}, isActive ? child.props.children : null);
        }
        return child;
    });

    return (
        <Tabs activeKey={activeKey} onSelect={(k) => onSelect(k as TKey)} className={styles.tabs}>
            {enhancedChildren}
        </Tabs>
    );
};
